import React, { useState, useEffect } from 'react';

const NetworkErrorBoundary = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasNetworkError, setHasNetworkError] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setHasNetworkError(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setHasNetworkError(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for unhandled fetch rejections instead of monkey-patching window.fetch
    const handleUnhandledRejection = event => {
      const error = event.reason;
      if (
        error &&
        (error.message?.includes('NetworkError') ||
          error.message?.includes('Failed to fetch') ||
          (error.name === 'TypeError' && !navigator.onLine))
      ) {
        setHasNetworkError(true);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  if (!isOnline || hasNetworkError) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-neutral-800 mb-2">
            Connection Issue
          </h2>

          <p className="text-neutral-600 mb-6">
            {!isOnline
              ? "You're currently offline. Please check your internet connection."
              : "We're having trouble connecting to our servers. Please try again."}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-neutral-800 text-white rounded-xl hover:bg-neutral-900 transition-colors"
            >
              Try Again
            </button>

            {hasNetworkError && (
              <button
                onClick={() => setHasNetworkError(false)}
                className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 transition-colors"
              >
                Continue Offline
              </button>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
            ></div>
            <span className="text-sm text-neutral-500">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default NetworkErrorBoundary;
